// FILE: src/components/QuestionFactory/GenerationPanel.js
//
// Demand-aware generation control + visibility (migration 107).
//
// Why this exists: before 107 the Question Factory screen exposed
// auto-publish ONLY. Generation actually lives in the qf-overnight GitHub
// Action, so turning things "off" in the app did nothing — which is how
// 9,139 unreviewed ACP/PMOCP drafts accumulated between 2026-06 and 2026-09
// with auto-publish already off. This panel surfaces the real switch and the
// full targeting decision behind it.
//
// Every number here comes from qf_generation_dashboard(), the same view the
// workflow consults via qf_next_generation_target(). There is no client-side
// re-derivation, so the panel can never disagree with what the generator does.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  getGenerationDashboard,
  getLengthTellSummary,
  setGenerationEnabled,
} from '../../services/questionFactoryService';

const STYLES = {
  card: {
    background: '#15152a', border: '1px solid #2a2a4a', borderRadius: '8px',
    padding: '16px 20px', marginBottom: '24px',
  },
  header: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' },
  title: { fontSize: '0.95rem', fontWeight: 600, color: '#eee' },
  hint: { fontSize: '0.78rem', color: '#aab', lineHeight: 1.5, marginBottom: '14px' },
  certBlock: {
    background: '#0f0f1e', border: '1px solid #2a2a4a', borderRadius: '6px',
    padding: '12px 14px', marginBottom: '10px',
  },
  certHead: { display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px', flexWrap: 'wrap' },
  cert: { fontWeight: 600, color: '#eee', textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '0.82rem' },
  pill: {
    fontSize: '0.68rem', padding: '2px 9px', borderRadius: '999px',
    border: '1px solid #444', color: '#ccd', letterSpacing: '0.3px',
  },
  pillOk: { background: '#1d3a26', border: '1px solid #3e8e52', color: '#9fe0b0' },
  pillBlocked: { background: '#2e2030', border: '1px solid #7a4a6a', color: '#e0aacb' },
  pillWarn: { background: '#3a3020', border: '1px solid #9a7a3a', color: '#e8cf95' },
  spacer: { flex: 1 },
  toggle: {
    background: '#3a3a6e', border: '1px solid #5a5a9e', color: '#fff',
    padding: '4px 12px', borderRadius: '4px', cursor: 'pointer', fontSize: '0.78rem',
  },
  toggleOn: { background: '#2e5a3a', border: '1px solid #4a9a5a' },
  busy: { opacity: 0.55, cursor: 'wait' },
  metrics: {
    display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(118px, 1fr))',
    gap: '8px', marginBottom: '10px',
  },
  metric: { background: '#15152a', border: '1px solid #23233e', borderRadius: '4px', padding: '7px 9px' },
  metricLabel: { fontSize: '0.62rem', color: '#8899aa', textTransform: 'uppercase', letterSpacing: '0.4px' },
  metricValue: { fontSize: '0.95rem', fontWeight: 600, color: '#ddd', marginTop: '2px' },
  ltHead: { marginTop: 18, paddingTop: 12, borderTop: '1px solid #333',
            fontSize: '0.9rem', fontWeight: 600, color: '#eee' },
  ltHint: { fontSize: '0.75rem', color: '#9a9a9a', margin: '4px 0 8px' },
  ltTable: { width: '100%', borderCollapse: 'collapse', fontSize: '0.75rem' },
  ltTh: { textAlign: 'right', padding: '3px 6px', color: '#9a9a9a', fontWeight: 500,
          borderBottom: '1px solid #333', whiteSpace: 'nowrap' },
  ltThL: { textAlign: 'left', padding: '3px 6px', color: '#9a9a9a', fontWeight: 500,
           borderBottom: '1px solid #333' },
  ltTd: { textAlign: 'right', padding: '3px 6px', color: '#ddd', whiteSpace: 'nowrap' },
  ltTdL: { textAlign: 'left', padding: '3px 6px', color: '#ddd' },
  ltFlag: { color: '#e5a54b', fontWeight: 600 },
  metricSub: { fontSize: '0.64rem', color: '#778', marginTop: '1px' },
  reason: { fontSize: '0.74rem', color: '#d0a8c0', marginBottom: '8px', lineHeight: 1.45 },
  reasonOk: { color: '#9fe0b0' },
  warnLine: { fontSize: '0.74rem', color: '#e8cf95', marginBottom: '8px', lineHeight: 1.45 },
  bucketsLabel: { fontSize: '0.62rem', color: '#8899aa', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: '4px' },
  bucketRow: { display: 'flex', gap: '8px', fontSize: '0.73rem', color: '#bbc', padding: '2px 0' },
  bucketName: { flex: 1 },
  bucketNums: { color: '#889', fontVariantNumeric: 'tabular-nums' },
  deficitPos: { color: '#f0b26b', fontWeight: 600, fontVariantNumeric: 'tabular-nums' },
  deficitZero: { color: '#6a7a6a', fontVariantNumeric: 'tabular-nums' },
  error: { color: '#ff6b6b', fontSize: '0.8rem', marginTop: '6px' },
  muted: { color: '#888', fontSize: '0.85rem' },
};

function num(v, fallback = '—') {
  if (v === null || v === undefined || v === '') return fallback;
  return String(v);
}

// Ratio thresholds come from qf_generation_config and are compared against each
// other by eye, so they must render at a fixed precision: num() is a
// null-guard, not a formatter, and passing it a digit count silently renders
// the count itself as the fallback.
function ratio(v, fallback = '\u2014') {
  const n = Number(v);
  if (v === null || v === undefined || v === '' || !Number.isFinite(n)) return fallback;
  return n.toFixed(2);
}

function queuePillStyle(state) {
  if (state === 'blocked') return { ...STYLES.pill, ...STYLES.pillBlocked };
  if (state === 'warning') return { ...STYLES.pill, ...STYLES.pillWarn };
  return STYLES.pill;
}

export default function GenerationPanel() {
  const [lengthTell, setLengthTell] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [dash, len] = await Promise.all([
        getGenerationDashboard(),
        // Defensive: a missing or misbehaving reader must degrade to "no
        // metrics", never take the whole panel down. Promise.resolve() wraps a
        // reader that returns undefined (as a partial jest mock does) or throws
        // synchronously -- both of which previously rejected the whole load and
        // rendered the panel's error state instead of the cert blocks.
        Promise.resolve()
          .then(() => (typeof getLengthTellSummary === 'function' ? getLengthTellSummary() : null))
          .catch(() => null),
      ]);
      setData(dash);
      setLengthTell(len);
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const flip = useCallback(async (examId, enabled) => {
    if (enabled && !window.confirm(
      `Turn generation ON for ${examId.toUpperCase()}?\n\n` +
      `The overnight workflow will generate for this certification whenever the ` +
      `demand model says it is eligible. It still stops automatically at the ` +
      `calculated target and at the unresolved-queue ceiling.`
    )) return;
    setBusyId(examId);
    setError(null);
    try {
      await setGenerationEnabled({ examId, enabled });
      await reload();
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setBusyId(null);
    }
  }, [reload]);

  const certs = useMemo(() => (data && Array.isArray(data.certs) ? data.certs : []), [data]);
  const config = (data && data.config) || {};

  return (
    <div style={STYLES.card} data-testid="qf-generation-panel">
      <div style={STYLES.header}>
        <div style={STYLES.title}>Generation</div>
        <button style={STYLES.toggle} onClick={reload} data-testid="qf-generation-refresh">
          Refresh
        </button>
      </div>
      <div style={STYLES.hint}>
        Generation is targeted by demand, not exam length. A certification is
        eligible only while it is below its <strong>baseline</strong> floor, or
        above it with enough learner demand to justify expansion. Generation stops
        automatically at the calculated target, and hard-stops at{' '}
        <code>{num(config.queue_ceiling, '100')}</code> unresolved queue items.
        Certifications listed as blocked are the expected steady state.
      </div>

      {loading && <div style={STYLES.muted}>Loading…</div>}
      {error && <div style={STYLES.error} data-testid="qf-generation-error">{error}</div>}

      {!loading && certs.map((c) => {
        const isBusy = busyId === c.exam_id;
        const buckets = Array.isArray(c.buckets) ? c.buckets : [];
        return (
          <div key={c.exam_id} style={STYLES.certBlock} data-testid={`qf-gen-cert-${c.exam_id}`}>
            <div style={STYLES.certHead}>
              <span style={STYLES.cert}>{c.exam_id}</span>
              <span
                style={c.eligible ? { ...STYLES.pill, ...STYLES.pillOk } : { ...STYLES.pill, ...STYLES.pillBlocked }}
                data-testid={`qf-gen-eligible-${c.exam_id}`}
              >
                {c.eligible ? 'eligible' : 'blocked'}
              </span>
              {c.below_baseline && <span style={STYLES.pill}>below baseline</span>}
              <span style={queuePillStyle(c.queue_state)} data-testid={`qf-gen-queue-${c.exam_id}`}>
                queue {num(c.queue_unresolved, 0)} · {num(c.queue_state)}
              </span>
              <span style={STYLES.pill}>priority {num(c.pressure, '0')}</span>
              <span style={STYLES.spacer} />
              <button
                data-testid={`qf-gen-toggle-${c.exam_id}`}
                style={{
                  ...STYLES.toggle,
                  ...(c.generation_enabled ? STYLES.toggleOn : {}),
                  ...(isBusy ? STYLES.busy : {}),
                }}
                disabled={isBusy}
                onClick={() => flip(c.exam_id, !c.generation_enabled)}
              >
                {isBusy ? 'Saving…' : c.generation_enabled ? 'Generation ON' : 'Generation OFF'}
              </button>
            </div>

            <div style={STYLES.metrics}>
              <div style={STYLES.metric}>
                <div style={STYLES.metricLabel}>Live bank</div>
                <div style={STYLES.metricValue}>{num(c.bank)}</div>
              </div>
              <div style={STYLES.metric}>
                <div style={STYLES.metricLabel}>Baseline target</div>
                <div style={STYLES.metricValue}>{num(c.baseline_target)}</div>
              </div>
              <div style={STYLES.metric}>
                <div style={STYLES.metricLabel}>Calculated target</div>
                <div style={STYLES.metricValue}>{num(c.calculated_target)}</div>
                <div style={STYLES.metricSub}>deficit {num(c.total_deficit, 0)}</div>
              </div>
              <div style={STYLES.metric}>
                <div style={STYLES.metricLabel}>Learners 90d</div>
                <div style={STYLES.metricValue}>{num(c.learners_90d, 0)}</div>
                <div style={STYLES.metricSub}>floor {num(c.min_learners_expansion)}</div>
              </div>
              <div style={STYLES.metric}>
                <div style={STYLES.metricLabel}>Attempts / question</div>
                <div style={STYLES.metricValue}>{num(c.attempts_per_question, '0')}</div>
                <div style={STYLES.metricSub}>{num(c.attempts_90d, 0)} in 90d</div>
              </div>
              <div style={STYLES.metric}>
                <div style={STYLES.metricLabel}>P90 exhaustion</div>
                <div style={STYLES.metricValue}>{num(c.p90_exhaustion_pct, '0')}%</div>
                <div style={STYLES.metricSub}>top {num(c.top_exhaustion_pct, '0')}%</div>
              </div>
              <div style={STYLES.metric}>
                <div style={STYLES.metricLabel}>Unresolved queue</div>
                <div style={STYLES.metricValue}>{num(c.queue_unresolved, 0)}</div>
                <div style={STYLES.metricSub}>ceiling {num(c.queue_ceiling)}</div>
              </div>
            </div>

            <div
              style={c.eligible ? { ...STYLES.reason, ...STYLES.reasonOk } : STYLES.reason}
              data-testid={`qf-gen-reason-${c.exam_id}`}
            >
              {c.eligible
                ? `Eligible — generating toward ${num(c.calculated_target)} (${num(c.total_deficit, 0)} to go).`
                : `Blocked — ${num(c.blocking_reason, 'no reason recorded')}.`}
            </div>

            {c.heavy_user_warning && (
              <div style={STYLES.warnLine} data-testid={`qf-gen-heavy-warn-${c.exam_id}`}>
                ⚠ {num(c.heavy_breach_count, 0)} learner(s) past the heavy-user
                threshold, below the quorum of {num(c.heavy_quorum_required)}. Surfaced
                for awareness — not enough to commission generation on its own.
              </div>
            )}

            {buckets.length > 0 && (
              <>
                <div style={STYLES.bucketsLabel}>
                  Blueprint deficits ({num(c.stratify_field)})
                </div>
                {buckets.map((b) => (
                  <div key={b.bucket} style={STYLES.bucketRow}>
                    <span style={STYLES.bucketName}>{b.bucket}</span>
                    <span style={STYLES.bucketNums}>
                      {num(b.live_count, 0)} / {num(b.bucket_target, 0)}
                    </span>
                    <span style={Number(b.deficit) > 0 ? STYLES.deficitPos : STYLES.deficitZero}>
                      {Number(b.deficit) > 0 ? `+${b.deficit}` : b.deficit}
                    </span>
                  </div>
                ))}
              </>
            )}
          </div>
        );
      })}

      {lengthTell && Array.isArray(lengthTell.rows) && lengthTell.rows.length > 0 && (
        <>
          <div style={STYLES.ltHead} data-testid="qf-length-tell">Answer-length tells</div>
          <div style={STYLES.ltHint} data-testid="qf-length-tell-hint">
            An item is auto-rejected only when the correct answer is the{' '}
            <strong>unique</strong> longest choice AND at least{' '}
            {ratio(lengthTell?.thresholds?.ratio_mean)}x the mean distractor AND at least{' '}
            {ratio(lengthTell?.thresholds?.ratio_next)}x the longest distractor. A merely
            slightly-longest correct answer still publishes, by design — chance alone puts the
            key longest about 25% of the time.
          </div>
          <table style={STYLES.ltTable}>
            <thead>
              <tr>
                <th style={STYLES.ltThL}>Cert</th>
                <th style={STYLES.ltThL}>Cohort</th>
                <th style={STYLES.ltTh}>n</th>
                <th style={STYLES.ltTh}>Longest</th>
                <th style={STYLES.ltTh}>Uniq longest</th>
                <th style={STYLES.ltTh}>Mean ratio</th>
                <th style={STYLES.ltTh}>Median ratio</th>
                <th style={STYLES.ltTh}>Conspicuous</th>
              </tr>
            </thead>
            <tbody>
              {lengthTell.rows.map((r) => (
                <tr key={`${r.exam_id}:${r.cohort}`}>
                  <td style={STYLES.ltTdL}>{r.exam_id}</td>
                  <td style={STYLES.ltTdL}>{r.cohort}</td>
                  <td style={STYLES.ltTd}>{num(r.n, 0)}</td>
                  <td style={STYLES.ltTd}>{num(r.correct_is_longest_pct, 1)}%</td>
                  <td style={STYLES.ltTd}>{num(r.unique_longest_pct, 1)}%</td>
                  <td style={STYLES.ltTd}>{num(r.mean_ratio, 3)}</td>
                  <td style={STYLES.ltTd}>{num(r.median_ratio, 3)}</td>
                  <td style={Number(r.conspicuous_pct) >= 5 ? STYLES.ltFlag : STYLES.ltTd}>
                    {num(r.conspicuous_pct, 1)}%
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}

      {!loading && certs.length === 0 && !error && (
        <div style={STYLES.muted}>No certifications configured.</div>
      )}
    </div>
  );
}
